import { useEffect, useState } from "react";
import { Activity, Eye, Phone, Mail, Inbox, AlertTriangle } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";

const RANGES = [{ d: 7, l: "7 jours" }, { d: 30, l: "30 jours" }];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);

  const load = () => api.get(`/admin/analytics/summary?days=${days}`).then((r) => setData(r.data));

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // refresh live traffic
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [days]);

  if (!data) return <div className="text-zinc-500">Chargement…</div>;

  const kpis = [
    { icon: Activity, label: "Visiteurs en direct", value: data.live_visitors, sub: "5 dernières minutes", live: true },
    { icon: Eye, label: "Vues de pages", value: data.total_views, sub: `sur ${days} jours` },
    { icon: Phone, label: "Clics téléphone", value: data.phone_clicks, sub: `sur ${days} jours` },
    { icon: Mail, label: "Clics email", value: data.email_clicks, sub: `sur ${days} jours` },
    { icon: Inbox, label: "Nouveaux leads", value: data.new_leads, sub: `${data.total_leads} au total` },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Tableau de bord</h1>
          <p className="text-zinc-500 text-sm mt-1">Trafic en temps réel & performances</p>
        </div>
        <div className="flex gap-2">
          {RANGES.map((r) => (
            <button key={r.d} onClick={() => setDays(r.d)} data-testid={`range-${r.d}`}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${days === r.d ? "bg-yellow-400 text-black" : "bg-white/5 text-zinc-400 border border-white/10"}`}>
              {r.l}
            </button>
          ))}
        </div>
      </div>

      {!data.smtp_configured && (
        <div className="mb-6 flex items-center gap-3 bg-yellow-400/10 border border-yellow-400/30 rounded-xl px-4 py-3 text-sm text-yellow-300" data-testid="smtp-warning">
          <AlertTriangle className="w-4 h-4" /> SMTP non configuré — les emails sont simulés. Renseignez les identifiants dans Paramètres serveur pour les activer.
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {kpis.map((k) => (
          <div key={k.label} data-testid={`kpi-${k.label}`} className="bg-[#18181B] border border-white/5 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <k.icon className="w-5 h-5 text-yellow-400" />
              {k.live && <span className="flex items-center gap-1.5 text-[10px] text-green-400"><span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" /> LIVE</span>}
            </div>
            <div className="font-display text-3xl font-black">{k.value}</div>
            <div className="text-sm text-zinc-300 mt-1">{k.label}</div>
            <div className="text-xs text-zinc-500">{k.sub}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[#18181B] border border-white/5 rounded-2xl p-6">
          <h3 className="font-bold mb-4">Trafic & interactions</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.series}>
              <defs>
                <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FFE240" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#FFE240" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={11} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#09090B", border: "1px solid #27272A", borderRadius: 12 }} />
              <Area type="monotone" dataKey="views" name="Vues" stroke="#FFE240" fill="url(#g)" strokeWidth={2} />
              <Area type="monotone" dataKey="phone" name="Tél" stroke="#22C55E" fill="transparent" strokeWidth={2} />
              <Area type="monotone" dataKey="email" name="Email" stroke="#3B82F6" fill="transparent" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-[#18181B] border border-white/5 rounded-2xl p-6">
          <h3 className="font-bold mb-4">Pages les plus vues</h3>
          {data.top_pages.length === 0 ? (
            <p className="text-zinc-500 text-sm">Aucune donnée pour l'instant.</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.top_pages} layout="vertical" margin={{ left: 10 }}>
                <XAxis type="number" stroke="#71717a" fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="path" stroke="#71717a" fontSize={10} width={90} />
                <Tooltip contentStyle={{ background: "#09090B", border: "1px solid #27272A", borderRadius: 12 }} />
                <Bar dataKey="views" fill="#FFE240" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
